/**
 * ClarityModeContext.js - GRANULLO CLARITY MODE
 * 
 * Clarity Mode is a reading posture that:
 * - Reduces distraction
 * - Emphasizes "What you committed to" vs "What reality is doing"
 * - Highlights Integrity Spine signals (drift/breach/evidence gates/scars)
 * - Does NOT change visual identity
 * 
 * Persisted in localStorage: granullo.clarityMode
 * When active, adds 'g-clarity' class to document.body
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ClarityModeContext = createContext(null);

const STORAGE_KEY = 'granullo.clarityMode';

export const ClarityModeProvider = ({ children }) => {
  const [clarityMode, setClarityMode] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'true';
    }
    return false;
  });

  // Sync with body class
  useEffect(() => {
    if (clarityMode) {
      document.body.classList.add('g-clarity');
    } else {
      document.body.classList.remove('g-clarity');
    }
  }, [clarityMode]);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, clarityMode.toString());
  }, [clarityMode]);

  const toggleClarityMode = useCallback(() => {
    setClarityMode(prev => !prev);
  }, []);

  const value = {
    clarityMode,
    setClarityMode,
    toggleClarityMode
  };

  return (
    <ClarityModeContext.Provider value={value}>
      {children}
    </ClarityModeContext.Provider>
  );
};

export const useClarityMode = () => {
  const context = useContext(ClarityModeContext);
  if (!context) {
    // Return default if not wrapped in provider (graceful fallback)
    return {
      clarityMode: false,
      setClarityMode: () => {},
      toggleClarityMode: () => {}
    };
  }
  return context;
};

export default ClarityModeContext;
